import {useMemo} from 'react';
import {shortenLargeNumbers} from '../../../../helpers/shortenLargeNumbers';
import {getMeta} from '../utils';

export const useCasesDetails = ({caseDetails}) => {
  const USER = useMemo(() => caseDetails?.transaction?.esd_user, [caseDetails]);
  const TRANSACTION = useMemo(() => caseDetails?.transaction, [caseDetails]);
  const ADDRESS = useMemo(
    () => caseDetails?.transaction?.event?.address,
    [caseDetails],
  );
  const PAYMENT = useMemo(
    () => caseDetails?.transaction?.payment,
    [caseDetails],
  );
  const SENDER = useMemo(
    () => caseDetails?.transaction?.senderAccount,
    [caseDetails],
  );

  const RECEIVER = useMemo(
    () => caseDetails?.transaction?.receiverAccount,
    [caseDetails],
  );
  const META = useMemo(
    () => caseDetails?.transaction?.event?.meta,
    [caseDetails],
  );

  const BEHAVIORAL_ANALYSIS = useMemo(
    () => caseDetails?.transaction?.behavior_analysis,
    [caseDetails?.transaction?.behavior_analysis],
  );

  const BEHAVIORAL_ANALYSIS_DETAILS = useMemo(
    () => [
      {
        leftText: 'Sufficient History',
        rightText: BEHAVIORAL_ANALYSIS?.has_sufficient_history
          ? 'True'
          : 'False',
      },
      {
        leftText: '1h Transaction Velocity',
        rightText: BEHAVIORAL_ANALYSIS?.transaction_velocity_1h || '0',
      },
      {
        leftText: '24h Transaction Velocity',
        rightText: BEHAVIORAL_ANALYSIS?.transaction_velocity_24h || '0',
      },
      {
        leftText: '7d Transaction Velocity',
        rightText: BEHAVIORAL_ANALYSIS?.transaction_velocity_7d || '0',
      },
      {
        leftText: 'Avg Transaction Amount',
        rightText: shortenLargeNumbers(
          BEHAVIORAL_ANALYSIS?.avg_transaction_amount || 0,
          TRANSACTION?.currency || 'NGN',
          {startAt: 'B'},
        ),
      },
      {
        leftText: 'Max Transaction Amount',
        rightText: shortenLargeNumbers(
          BEHAVIORAL_ANALYSIS?.max_transaction_amount || 0,
          TRANSACTION?.currency || 'NGN',
          {startAt: 'B'},
        ),
      },
      {
        leftText: 'Amount Standard Deviation',
        rightText: shortenLargeNumbers(
          BEHAVIORAL_ANALYSIS?.amount_std_dev || 0,
          TRANSACTION?.currency || 'NGN',
          {startAt: 'B'},
        ),
      },
      {
        leftText: 'High Risk Ratio',
        rightText: BEHAVIORAL_ANALYSIS?.high_risk_ratio
          ? `${(BEHAVIORAL_ANALYSIS.high_risk_ratio * 100).toFixed(2)}%`
          : '0%',
      },
      {
        leftText: 'Block Ratio',
        rightText: BEHAVIORAL_ANALYSIS?.block_ratio
          ? `${(BEHAVIORAL_ANALYSIS.block_ratio * 100).toFixed(2)}%`
          : '0%',
      },
      {
        leftText: 'Unusual Amount',
        rightText: BEHAVIORAL_ANALYSIS?.is_amount_unusual ? 'True' : 'False',
      },
      {
        leftText: 'Night Time Transaction',
        rightText: BEHAVIORAL_ANALYSIS?.is_night_time ? 'True' : 'False',
      },
      {
        leftText: 'Weekend Transaction',
        rightText: BEHAVIORAL_ANALYSIS?.is_weekend ? 'True' : 'False',
      },
      {
        leftText: 'New User',
        rightText: BEHAVIORAL_ANALYSIS?.is_new_user ? 'True' : 'False',
      },
    ],
    [BEHAVIORAL_ANALYSIS, TRANSACTION?.currency],
  );

  const OTHER_DETAILS = getMeta(META);

  const TRANSACTION_ANALYSIS = useMemo(
    () =>
      Object.entries(caseDetails?.transaction?.event || {})
        .map(([key, value]) => {
          if (!['meta'].includes(key)) {
            return {
              label: key?.replaceAll('_', ' '),
              value: Object.entries(value || {})
                .map(
                  ([vKey, vValue]) =>
                    !['id', 'user_id'].includes(vKey) && {
                      leftText: vKey?.replaceAll('_', ' '),
                      rightText:
                        typeof vValue !== 'object' && vValue?.toString(),
                    },
                )
                .filter(Boolean),
            };
          }
        })
        .filter(Boolean),
    [caseDetails?.transaction?.event],
  );

  return {
    USER,
    TRANSACTION,
    ADDRESS,
    PAYMENT,
    SENDER,
    RECEIVER,
    META,
    BEHAVIORAL_ANALYSIS_DETAILS,
    TRANSACTION_ANALYSIS,
    OTHER_DETAILS,
  };
};
